import * as React from 'react';
import { Pencil, Trash2, Search, ChevronRight } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

import { Card } from '@/components/ui/card';
import { useGetBatchQuery, useDeleteBatchMutation } from '../store/batchApi';
import { useSelector } from 'react-redux';
import BatchDialog from './Batch/BatchDialog';
import { CustomAlertDialog } from './AlertDialog';
import { RootState } from '@/types';
import Pagination from './Pagination';
import { useNavigate } from 'react-router-dom';

interface Batch {
  _id: number;
  bCode: string;
  courseName: string;
  startTime: string;
  endTime: string;
  topic: string;
  // startDate: string;
  // endDate?: string;
}

const BatchCreationDialog = () => {
  const { data: batches } = useGetBatchQuery();
  const [deleteBatch] = useDeleteBatchMutation();
  const { user } = useSelector((state: RootState) => state.user);

  const [searchTerm, setSearchTerm] = React.useState('');
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editBatch, setEditBatch] = React.useState<Batch | null>(null);
  const navigate = useNavigate()
  const filteredBatches = batches?.filter(
    (batch: Batch) =>
      batch.bCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      batch.courseName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEditBatch = (batch: Batch) => {
    setEditBatch(batch);
    setDialogOpen(true);
  };

  return (
    <Pagination data={filteredBatches} itemsPerPage={10}>
      {(batchesPerPageData) => (
        <Card className="w-full overflow-hidden bg-white shadow-lg">
          <div className="p-4 sm:p-6 space-y-6">
            {/* Header Section */}

            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="flex flex-col gap-1">
                <h1 className="text-2xl font-bold text-foreground">
                  Batch Management
                </h1>
                <div className="flex items-center text-sm text-gray-500">
                  <span onClick={() => navigate("/")}>Dashboard</span>
                  <ChevronRight className="w-4 h-4 mx-1" />
                  <span className="text-green-600 font-medium">Batch</span>
                </div>
              </div>
            </div>

            {/* Search and Actions Section */}
            <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
              <div className="relative flex-grow max-w-md group">
                <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                  <Search className="w-5 h-5 text-gray-400 group-focus-within:text-green-500 trantion-colors" />
                </div>
                <Input
                  placeholder="Search batches..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 h-11 w-full rounded-lg border-gray-200 focus:border-green-500 focus:ring-green-500 transition-all duration-300 "
                />
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setSearchTerm('')}
                  className="h-11 px-4 border-gray-200 hover:bg-green-50 hover:text-green-600 hover:border-green-200 transition-all duration-300"
                >
                  Clear
                </Button>

                {/* Integrated Creation Dialog */}

                <BatchDialog
                  data={editBatch}
                  setEdit={setEditBatch}
                  dialogOpen={dialogOpen}
                  setDialogOpen={setDialogOpen}
                />
              </div>
            </div>

            {/* Table Section */}
            <div className="overflow-x-auto rounded-lg border animate-fadeIn">
              <div className="inline-block min-w-full align-middle">
                <table className="min-w-full divide-y ">
                  <thead className="bg-muted">
                    <tr>
                      {[
                        '#',
                        'Batch Code',
                        'Course Name',
                        'Topic',
                        'Batch Time',
                        'Actions',
                      ].map((header) => (
                        <th
                          key={header}
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className=" divide-y">
                    {batchesPerPageData?.map((batch: Batch, index: number) => (
                      <tr
                        key={batch._id}
                        className="hover:bg-muted transition-colors duration-300 animate-slideIn"
                        style={{ animationDelay: `${index * 100}ms` }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                          {index + 1}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-foreground hover:text-green-600 transition-colors duration-300">
                            {batch.bCode}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                          {batch.courseName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                          {batch.topic}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                          {batch.startTime && batch.endTime ? (
                            <>
                              {new Date(
                                `1970-01-01T${batch.startTime}:00`
                              ).toLocaleTimeString([], {
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true,
                              })}{' '}
                              -{' '}
                              {new Date(
                                `1970-01-01T${batch.endTime}:00`
                              ).toLocaleTimeString([], {
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true,
                              })}
                            </>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                          <div className="flex gap-2">
                            {(user?.role === 'hr' ||
                              user?.role === 'instructor') && (
                                <button
                                  className="p-1 rounded-full hover:bg-blue-50 text-blue-600 transition-all duration-300 transform hover:scale-110"
                                  onClick={() => handleEditBatch(batch)}
                                >
                                  <Pencil className="w-4 h-4" />
                                </button>
                              )}

                            <CustomAlertDialog
                              triggerButton={
                                <button className="p-1 rounded-full hover:bg-red-50 text-red-600 transition-all duration-300 transform hover:scale-110">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              }
                              title="Delete Batch"
                              description="Are you sure you want to delete this batch?"
                              onConfirm={() =>
                                deleteBatch({ id: batch._id, Token: user?.token })
                              }
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {/* <div className="flex justify-center gap-2">
          {[1, 2].map((page) => (
            <Button
              key={page}
              variant="outline"
              className={`w-10 h-10 p-0 transition-all duration-300 ${
                page === 1
                  ? 'bg-green-600 text-white hover:bg-green-700 border-green-600'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {page}
            </Button>
          ))}
        </div> */}
          </div>
        </Card>
      )}
    </Pagination>
  );
};

export default BatchCreationDialog;
